import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FpaActualChannel, FpaResultEffect } from '@loot/shared';
import { api } from '../../api.ts';
import { FormModal } from '../../components/FormModal.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  PageHeader,
  Panel,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { useFormat } from '../purchasing/common.tsx';

const KEY = ['controlling', 'actual-sources'];

interface Overview {
  origins: {
    id: string;
    code: string;
    name: string;
    channel: FpaActualChannel;
    ownerLabel: string;
    isActive: boolean;
  }[];
  rules: {
    id: string;
    originId: string;
    expenseTypeName: string | null;
    classificationName: string | null;
    resultEffect: FpaResultEffect;
  }[];
  cutovers: {
    id: string;
    legalEntityId: string;
    entityCode: string;
    firstMonth: string;
    status: 'active' | 'cancelled';
    reason: string;
    cancelReason: string | null;
  }[];
  entities: { id: string; code: string; name: string }[];
  options: {
    expenseTypes: { id: string; name: string }[];
    classifications: { id: string; name: string }[];
  };
  sheetOutside: {
    outcome: 'flow_source' | 'origin_missing';
    legalEntityId: string;
    competence: string;
    rows: number;
    amount: string;
  }[];
  actions: { manage: boolean };
}

type Form =
  | { kind: 'origin' }
  | { kind: 'rule' }
  | { kind: 'cutover' }
  | { kind: 'cancel'; id: string; label: string };

/**
 * Origens do Realizado e mês de virada (ADR 0098): diz de onde vem cada par e, por empresa, a partir
 * de qual competência o fluxo vale. É o que impede o mesmo gasto de entrar pela planilha e pela fila.
 */
export function ActualSourcesPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [form, setForm] = useState<Form | null>(null);
  const data = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<Overview>('/fpa/actual-sources'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.post(`/fpa/actual-sources/rules/${id}/deactivate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const o = data.data;
  const originOf = new Map(o.origins.map((x) => [x.id, x]));
  const codeOf = new Map(o.entities.map((e) => [e.id, e.code]));
  const manage = o.actions.manage;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.controllingSources')}
        description={t('controlling.sources.about')}
      />
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">{t('controlling.sources.cutovers')}</h2>
          {manage && (
            <Button onClick={() => setForm({ kind: 'cutover' })}>
              {t('controlling.sources.activate')}
            </Button>
          )}
        </div>
        <p className="text-sm text-muted">{t('controlling.sources.cutoverAbout')}</p>
        <Table
          head={[
            t('controlling.queue.fields.entity'),
            t('controlling.sources.firstMonth'),
            t('controlling.queue.fields.state'),
            t('controlling.queue.fields.note'),
            '',
          ]}
        >
          {o.cutovers.length === 0 && (
            <EmptyRow colSpan={5}>{t('controlling.sources.noCutover')}</EmptyRow>
          )}
          {o.cutovers.map((c) => (
            <tr key={c.id} className="border-b border-rule last:border-0">
              <td className="px-4 py-2.5">{c.entityCode}</td>
              <td className="px-4 py-2.5">{fmt.month(c.firstMonth)}</td>
              <td className="px-4 py-2.5">
                <Badge tone={c.status === 'active' ? 'ok' : 'neutral'}>
                  {t(`controlling.sources.cutoverStatus.${c.status}`)}
                </Badge>
              </td>
              <td className="px-4 py-2.5 text-xs text-muted">{c.cancelReason ?? c.reason}</td>
              <td className="px-4 py-2.5 text-right">
                {manage && c.status === 'active' && (
                  <Button
                    variant="secondary"
                    onClick={() => setForm({ kind: 'cancel', id: c.id, label: c.entityCode })}
                  >
                    {t('controlling.sources.cancel')}
                  </Button>
                )}
                <MasterDeleteButton table="fpa.actual_cutover" id={c.id} />
              </td>
            </tr>
          ))}
        </Table>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">{t('controlling.sources.origins')}</h2>
          {manage && (
            <Button variant="secondary" onClick={() => setForm({ kind: 'origin' })}>
              {t('controlling.sources.newOrigin')}
            </Button>
          )}
        </div>
        <Table
          head={[
            t('controlling.sources.origin'),
            t('controlling.sources.channel'),
            t('controlling.sources.owner'),
            '',
          ]}
        >
          {o.origins.map((x) => (
            <tr key={x.id} className="border-b border-rule last:border-0">
              <td className="px-4 py-2.5">{x.name}</td>
              <td className="px-4 py-2.5">
                <Badge tone={x.channel === 'flow' ? 'ok' : 'neutral'}>
                  {t(`controlling.sources.channels.${x.channel}`)}
                </Badge>
              </td>
              <td className="px-4 py-2.5">{x.ownerLabel}</td>
              <td className="px-4 py-2.5 text-right">
                <MasterDeleteButton table="fpa.actual_origin" id={x.id} />
              </td>
            </tr>
          ))}
        </Table>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">{t('controlling.sources.rules')}</h2>
          {manage && (
            <Button variant="secondary" onClick={() => setForm({ kind: 'rule' })}>
              {t('controlling.sources.newRule')}
            </Button>
          )}
        </div>
        <p className="text-sm text-muted">{t('controlling.sources.rulesAbout')}</p>
        {remove.isError && <ErrorBox error={remove.error} />}
        <Table
          head={[
            t('controlling.sources.pair'),
            t('controlling.sources.origin'),
            t('controlling.sources.effect'),
            '',
          ]}
        >
          {o.rules.length === 0 && (
            <EmptyRow colSpan={4}>{t('controlling.sources.noRules')}</EmptyRow>
          )}
          {o.rules.map((r) => (
            <tr key={r.id} className="border-b border-rule last:border-0">
              <td className="px-4 py-2.5">
                {r.expenseTypeName}
                <span className="block text-xs text-muted">
                  {r.classificationName ?? t('controlling.sources.allClassifications')}
                </span>
              </td>
              <td className="px-4 py-2.5">{originOf.get(r.originId)?.name}</td>
              <td className="px-4 py-2.5">{t(`controlling.sources.effects.${r.resultEffect}`)}</td>
              <td className="px-4 py-2.5 text-right">
                {manage && (
                  <Button variant="secondary" onClick={() => remove.mutate(r.id)}>
                    {t('controlling.sources.removeRule')}
                  </Button>
                )}
                <MasterDeleteButton table="fpa.actual_origin_rule" id={r.id} />
              </td>
            </tr>
          ))}
        </Table>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-base font-semibold">{t('controlling.sources.sheetOutside')}</h2>
        <p className="text-sm text-muted">{t('controlling.sources.sheetOutsideAbout')}</p>
        <Table
          head={[
            t('controlling.queue.fields.competence'),
            t('controlling.queue.fields.entity'),
            t('controlling.sources.reason'),
            { label: t('controlling.sources.rows'), align: 'right' },
            { label: t('controlling.sources.amount'), align: 'right' },
          ]}
        >
          {o.sheetOutside.length === 0 && (
            <EmptyRow colSpan={5}>{t('controlling.sources.noSheetOutside')}</EmptyRow>
          )}
          {o.sheetOutside.map((s) => (
            <tr
              key={`${s.outcome}-${s.legalEntityId}-${s.competence}`}
              className="border-b border-rule last:border-0"
            >
              <td className="px-4 py-2.5">{fmt.month(s.competence)}</td>
              <td className="px-4 py-2.5">{codeOf.get(s.legalEntityId)}</td>
              <td className="px-4 py-2.5">{t(`controlling.sources.outcomes.${s.outcome}`)}</td>
              <NumCell>{s.rows}</NumCell>
              <NumCell>{fmt.money(s.amount, 'BRL')}</NumCell>
            </tr>
          ))}
        </Table>
      </section>
      {form && <SourceForm form={form} data={o} onClose={() => setForm(null)} />}
      <Panel>
        <p className="text-sm text-muted">{t('controlling.sources.ledgerNote')}</p>
      </Panel>
    </div>
  );
}

function SourceForm({ form, data, onClose }: { form: Form; data: Overview; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [v, setV] = useState<Record<string, string>>({
    channel: 'sheet',
    resultEffect: 'result',
    firstMonth: '2026-11',
  });
  const set = (k: string) => (e: { target: { value: string } }) =>
    setV({ ...v, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      if (form.kind === 'origin') {
        return api.post('/fpa/actual-sources/origins', {
          code: v.code,
          name: v.name,
          channel: v.channel,
          ownerLabel: v.ownerLabel,
        });
      }
      if (form.kind === 'rule') {
        return api.post('/fpa/actual-sources/rules', {
          originId: v.originId,
          expenseTypeId: v.expenseTypeId,
          classificationId: v.classificationId || null,
          resultEffect: v.resultEffect,
        });
      }
      if (form.kind === 'cutover') {
        return api.post('/fpa/actual-sources/cutovers', {
          legalEntityId: v.legalEntityId,
          firstMonth: `${v.firstMonth}-01`,
          reason: v.reason,
        });
      }
      return api.post(`/fpa/actual-sources/cutovers/${form.id}/cancel`, { note: v.reason });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['controlling'] });
      onClose();
    },
  });
  const titles = {
    origin: t('controlling.sources.newOrigin'),
    rule: t('controlling.sources.newRule'),
    cutover: t('controlling.sources.activate'),
    cancel: t('controlling.sources.cancel'),
  };
  return (
    <FormModal
      open
      title={titles[form.kind]}
      description={form.kind === 'cancel' ? form.label : undefined}
      size="md"
      dirty={Object.keys(v).length > 3}
      saving={save.isPending}
      error={save.error}
      saveLabel={titles[form.kind]}
      saveTone={form.kind === 'cancel' ? 'danger' : 'primary'}
      onSave={() => save.mutate()}
      onClose={onClose}
    >
      {form.kind === 'origin' && (
        <>
          <Field label={t('controlling.sources.code')} required>
            <Input id="origin-code" value={v.code ?? ''} onChange={set('code')} />
          </Field>
          <Field label={t('controlling.sources.originName')} required>
            <Input id="origin-name" value={v.name ?? ''} onChange={set('name')} />
          </Field>
          <Field label={t('controlling.sources.channel')} required>
            <Select id="origin-channel" value={v.channel} onChange={set('channel')}>
              <option value="sheet">{t('controlling.sources.channels.sheet')}</option>
              <option value="flow">{t('controlling.sources.channels.flow')}</option>
            </Select>
          </Field>
          <Field label={t('controlling.sources.owner')} required>
            <Input id="origin-owner" value={v.ownerLabel ?? ''} onChange={set('ownerLabel')} />
          </Field>
        </>
      )}
      {form.kind === 'rule' && (
        <>
          <Field label={t('controlling.sources.origin')} required>
            <Select id="rule-origin" value={v.originId ?? ''} onChange={set('originId')}>
              <option value="">{t('finance.payablesPage.choose')}</option>
              {data.origins
                .filter((o) => o.isActive)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label={t('controlling.sources.expenseType')} required>
            <Select id="rule-type" value={v.expenseTypeId ?? ''} onChange={set('expenseTypeId')}>
              <option value="">{t('finance.payablesPage.choose')}</option>
              {data.options.expenseTypes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('controlling.sources.classification')}>
            <Select
              id="rule-classification"
              value={v.classificationId ?? ''}
              onChange={set('classificationId')}
            >
              <option value="">{t('controlling.sources.allClassifications')}</option>
              {data.options.classifications.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('controlling.sources.effect')} required>
            <Select id="rule-effect" value={v.resultEffect} onChange={set('resultEffect')}>
              <option value="result">{t('controlling.sources.effects.result')}</option>
              <option value="outside_result">
                {t('controlling.sources.effects.outside_result')}
              </option>
            </Select>
          </Field>
        </>
      )}
      {form.kind === 'cutover' && (
        <>
          <Field label={t('controlling.queue.fields.entity')} required>
            <Select
              id="cutover-entity"
              value={v.legalEntityId ?? ''}
              onChange={set('legalEntityId')}
            >
              <option value="">{t('finance.payablesPage.choose')}</option>
              {data.entities.map((e) => (
                <option key={e.id} value={e.id}>
                  {`${e.code} · ${e.name}`}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label={t('controlling.sources.firstMonth')}
            hint={t('controlling.sources.firstMonthHint')}
            required
          >
            <Input
              id="cutover-month"
              type="month"
              value={v.firstMonth}
              onChange={set('firstMonth')}
            />
          </Field>
        </>
      )}
      {(form.kind === 'cutover' || form.kind === 'cancel') && (
        <Field className="col-span-full" label={t('controlling.queue.fields.note')} required>
          <Textarea id="cutover-reason" rows={2} value={v.reason ?? ''} onChange={set('reason')} />
        </Field>
      )}
    </FormModal>
  );
}
